/**
 * The HUD message line: sim events, combat events, and the few state
 * transitions neither feed carries, turned into the text the pilot reads.
 *
 * Where `docs/game/ui-menus.md` and `docs/game/flight-model.md` recovered the
 * original's own wording from the `RB.EXE` string dump, that exact string is
 * used — `YOUR PLANE IS STALLED!`, `You've been wounded!`, `PILOT IS DEAD`,
 * `YOU'RE LOW ON FUEL.`, `YOU'RE DANGEROUSLY LOW.`, and the attitude callouts
 * in `hud-format.ts`. The rest are port-side wording in the same voice: the
 * string dump has no recovered text for spins, wing failure, carburettor ice
 * or the jam messages the manual only describes in prose ("You will receive
 * an on-screen message indicating this...").
 */

import { OUTCOME_BANNERS } from "./combat-hud.ts";
import {
  aircraftLabel,
  metresToFeet,
  nationOf,
  underFlakFire,
  type CombatEvent,
  type CombatEventKind,
  type EncounterSide,
  type EncounterState,
  type MissionType,
  type Nation,
  type SimEventKind,
  type SimState,
} from "@red-opus/engine";
import {
  ABOUT_TO_STALL,
  AIRCRAFT_KILL_NOUN,
  BEING_SHOT_AT,
  ENEMY_SPOTTED,
  FLAK_SPOTTED,
  FUEL_LINES,
  FUEL_LINE_HEALTH,
  GUNS_UNJAMMED,
  KILL_NOUNS,
  LOW_AMMUNITION,
  LOW_AMMUNITION_FRACTION,
  MISSION_COMPLETE,
  NEARING_MESSAGES,
  NOW_FLIGHT_LEADER,
  OIL_PRESSURE,
  OIL_PRESSURE_HEALTH,
  OOPS,
  SCRATCH_ONE,
  SHOT_AT_ONE_OF_OURS,
  SHOT_DOWN,
  WINGS_SHAKING,
  WING_SHAKE_FRACTION,
  ZEPPELIN_DESTROYED,
  ammunitionFraction,
  enemyInSight,
  nationalityWordOf,
  nearStall,
  seniorSurvivor,
  underAimedFire,
  wingLoadFraction,
} from "./sim-callouts.ts";

export const SIM_EVENT_MESSAGES: Readonly<Record<SimEventKind, string>> = {
  stallEntered: "YOUR PLANE IS STALLED!",
  stallRecovered: "STALL RECOVERED.",
  spinEntered: "YOU'RE IN A SPIN!",
  spinRecovered: "SPIN RECOVERED.",
  spiralEntered: "YOU'RE IN A SPIRAL DIVE!",
  wingFailure: "YOUR WINGS HAVE BROKEN!",
  gunJammed: "YOUR GUNS ARE JAMMED!",
  gunJamPermanent: "YOUR GUNS ARE JAMMED PERMANENTLY!",
  // `RB.EXE` 604-605, replacing the port's own placeholder (G33): the original
  // has a real string for this and it is much better than "GUNS CLEARED."
  gunCleared: GUNS_UNJAMMED,
  carburettorIced: "YOUR CARBURETOR IS FROZEN!",
  carburettorThawed: "YOUR CARBURETOR HAS THAWED.",
  fuelExhausted: "YOU'RE OUT OF FUEL.",
  hypoxiaDizzy: "YOU'RE FEELING DIZZY.",
  hypoxiaBlackout: "YOU'VE BLACKED OUT!",
  hypoxiaRecovered: "YOU'RE COMING AROUND.",
  pilotWounded: "You've been wounded!",
  bleedOutWarning: "YOUR WOUND IS GETTING WORSE.",
  pilotDied: "PILOT IS DEAD",
  groundContact: "YOU'RE ON THE GROUND.",
  liftedOff: "YOU'RE AIRBORNE.",
  gearCollapsed: "YOUR UNDERCARRIAGE HAS COLLAPSED!",
  landed: "YOU'RE DOWN SAFELY.",
  crashed: "YOU HAVE CRASHED.",
};

export function messageForEvent(kind: SimEventKind): string {
  return SIM_EVENT_MESSAGES[kind];
}

/**
 * Combat feedback, split by whether it happened to the player or to somebody
 * else — "YOU'RE HIT!" and "HITS!" are the same event seen from two cockpits.
 * `null` means the event is not worth a line from that side.
 */
interface CombatPhrasing {
  readonly own: string | null;
  readonly other: string | null;
}

export const COMBAT_EVENT_MESSAGES: Readonly<Record<CombatEventKind, CombatPhrasing>> = {
  roundsOnTarget: { own: "HITS!", other: null },
  takingHits: { own: "YOU'RE HIT!", other: null },
  engineSputtering: { own: "YOUR ENGINE IS SPUTTERING!", other: null },
  onFire: { own: "YOU'RE ON FIRE!", other: "HE'S BURNING!" },
  outOfControl: { own: null, other: "HE'S GOING DOWN!" },
  destroyed: { own: null, other: "ENEMY DESTROYED" },
  encounterWon: { own: "ENEMY DESTROYED", other: null },
  encounterLost: { own: "YOU'VE BEEN SHOT DOWN", other: null },
};

/**
 * The original's own line for a shell rather than a bullet, verbatim from
 * `RB.EXE`'s string pool at file `0x45600` — it sits in `s\combat\aa.c`'s
 * literal block, one entry away from the plain "You've been hit!", which is
 * how we know the game distinguishes the two.
 */
export const FLAK_HIT_MESSAGE = "YOU'VE BEEN HIT BY FLAK!";

/**
 * The line one combat event deserves from the player's point of view, if any.
 * `groundFire` is what separates archie from gunfire: the event kind is the
 * same `takingHits` either way, because to the damage model a shell fragment
 * and a bullet are the same thing arriving.
 */
export function messageForCombatEvent(
  event: CombatEvent,
  playerId: string,
  groundFire = false,
): string | null {
  if (groundFire && event.kind === "takingHits" && event.aircraftId === playerId) {
    return FLAK_HIT_MESSAGE;
  }
  const phrasing = COMBAT_EVENT_MESSAGES[event.kind];
  return event.aircraftId === playerId ? phrasing.own : phrasing.other;
}

/** Fuel remaining, as a fraction of this aircraft's tank. */
export function fuelFraction(state: SimState): number {
  const capacity = state.config.spec.fuel.capacityKg;
  return capacity > 0 ? Math.min(Math.max(state.engine.fuelKg / capacity, 0), 1) : 0;
}

export const LOW_FUEL_FRACTION = 0.2;
/**
 * `YOU'RE DANGEROUSLY LOW.` is listed in the string dump next to the low-fuel
 * warning with the trigger recorded as "fuel or altitude" — ambiguous in the
 * source. Read here as the altitude warning, which is the reading that makes
 * both strings distinct and useful.
 */
export const DANGEROUSLY_LOW_FT = 200;

function crossedBelow(previous: number, next: number, threshold: number): boolean {
  return previous >= threshold && next < threshold;
}

/**
 * The two threshold-warning strings, exported so `audio/sfx-map.ts` can key its cue lookup off
 * the same literals `derivedMessages` actually produces, rather than duplicating them.
 */
export const LOW_FUEL_MESSAGE = "YOU'RE LOW ON FUEL.";
export const DANGEROUSLY_LOW_MESSAGE = "YOU'RE DANGEROUSLY LOW.";

/**
 * The second tier of `RB.EXE`'s own envelope messages (G33), each fired the tick
 * its predicate first becomes true and not again until it has gone false. The
 * predicates live in `sim-callouts.ts` with their evidence; this is only the
 * diffing, which is the same crossing rule the two fuel/altitude warnings use.
 *
 * `You feel faint.` / `You regain conciousness and seize the controls!` are
 * deliberately **not** here: the port already emits its own hypoxia lines from
 * real `SimEventKind`s (`hypoxiaDizzy`, `hypoxiaRecovered`), and swapping in
 * the original's words is a separate judgement about whether those two events
 * are the same thing the original is describing. Wiring a recovered string to
 * the wrong trigger is worse than leaving it unwired.
 */
function envelopeCallouts(previous: SimState, next: SimState): readonly string[] {
  const messages: string[] = [];
  const rose = (was: boolean, is: boolean): boolean => !was && is;

  const wingWas = wingLoadFraction(previous);
  const wingIs = wingLoadFraction(next);
  if (wingIs !== null && wingWas !== null && rose(wingWas >= WING_SHAKE_FRACTION, wingIs >= WING_SHAKE_FRACTION)) {
    messages.push(WINGS_SHAKING);
  }
  if (rose(nearStall(previous), nearStall(next))) messages.push(ABOUT_TO_STALL);
  if (crossedBelow(previous.damage.engine, next.damage.engine, OIL_PRESSURE_HEALTH)) {
    messages.push(OIL_PRESSURE);
  }
  if (crossedBelow(previous.damage.fuel, next.damage.fuel, FUEL_LINE_HEALTH)) messages.push(FUEL_LINES);
  if (crossedBelow(ammunitionFraction(previous), ammunitionFraction(next), LOW_AMMUNITION_FRACTION)) {
    messages.push(LOW_AMMUNITION);
  }
  return messages;
}

/**
 * Messages for things neither event feed reports: the two threshold warnings.
 *
 * The wound used to be detected here too, by diffing `pilot.wounded` across the
 * tick, because nothing could actually wound a pilot yet. Combat can now, and
 * it goes through the engine's `applyWound`, which stamps a real `pilotWounded`
 * event — so the manual check has been retired rather than left to fire the
 * same line twice (see docs/engineering/lessons.md, world-rendering lane).
 */
export function derivedMessages(previous: SimState, next: SimState): readonly string[] {
  const messages: string[] = [];
  if (crossedBelow(fuelFraction(previous), fuelFraction(next), LOW_FUEL_FRACTION)) {
    messages.push(LOW_FUEL_MESSAGE);
  }
  if (
    next.readout.climbRateMps < 0 &&
    crossedBelow(metresToFeet(previous.position.y), metresToFeet(next.position.y), DANGEROUSLY_LOW_FT)
  ) {
    messages.push(DANGEROUSLY_LOW_MESSAGE);
  }
  return [...messages, ...envelopeCallouts(previous, next)];
}

/** Every message one tick produced, engine events first, in the order they should scroll. */
export function messagesForTick(previous: SimState, next: SimState): readonly string[] {
  return [...next.events.map(messageForEvent), ...derivedMessages(previous, next)];
}

/**
 * The situational half (G33), diffed across the tick.
 *
 * The nation decides only which of `RB.EXE`'s two flak strings prints, and it is
 * read off the player's own aeroplane rather than taken from a mission: a German
 * pilot flies a German machine, so the instant dogfight — which has no mission
 * at all — still gets the right one of the pair.
 */
function situationalCallouts(
  previous: EncounterState,
  next: EncounterState,
  playerId: string,
  nation: Nation,
): readonly string[] {
  const messages: string[] = [];
  const rose = (was: boolean, is: boolean): boolean => !was && is;
  if (rose(enemyInSight(previous, playerId), enemyInSight(next, playerId))) messages.push(ENEMY_SPOTTED);
  if (rose(underFlakFire(previous.flak, playerId), underFlakFire(next.flak, playerId))) {
    messages.push(FLAK_SPOTTED[nation]);
  }
  if (rose(underAimedFire(previous, playerId), underAimedFire(next, playerId))) messages.push(BEING_SHOT_AT);
  if (rose(seniorSurvivor(previous, playerId), seniorSurvivor(next, playerId))) messages.push(NOW_FLIGHT_LEADER);
  return messages;
}

/**
 * Kill feedback in the original's own words (G33 / M9). The port printed a flat
 * `ENEMY DESTROYED` for every kill regardless of target or side; `RB.EXE` names
 * the aeroplane, names its nationality, and tells the pilot about friendly fire
 * in the cockpit instead of leaving him to find out at the Board of Inquiry.
 *
 * Only the player's own kills are re-worded. Somebody else's is still
 * `HE'S GOING DOWN!` from the combat feed, which is what a wingman would say.
 */
function aircraftKills(next: EncounterState, playerId: string, playerSide: EncounterSide): readonly string[] {
  return next.events.flatMap((event) => {
    if (event.kind !== "destroyed" || event.sourceId !== playerId) return [];
    const victim = next.aircraft.find((entry) => entry.id === event.aircraftId);
    if (!victim || victim.id === playerId) return [];
    const aircraft = victim.sim.config.aircraft;
    if (victim.side === playerSide) return [`${OOPS}${AIRCRAFT_KILL_NOUN}`];
    return [`${SHOT_DOWN}${nationalityWordOf(aircraft)} ${aircraftLabel(aircraft)}`];
  });
}

/**
 * The static targets. They carry no `CombatEvent` of their own — a target is a
 * record with a `destroyed` flag and a `lastAttackerId` — so the kill is read as
 * the flag turning over with the player named on it.
 *
 * A kind with no noun in `pyro.c`'s table gets no line, exactly as the
 * original's NULL entries produce none (`KILL_NOUNS`).
 */
function targetKills(
  previous: EncounterState,
  next: EncounterState,
  playerId: string,
  playerSide: EncounterSide,
): readonly string[] {
  const before = new Map(previous.targets.map((target) => [target.id, target.destroyed]));
  return next.targets.flatMap((target) => {
    if (!target.destroyed || before.get(target.id) !== false) return [];
    if (target.lastAttackerId !== playerId) return [];
    if (target.kind === "zeppelin") return [ZEPPELIN_DESTROYED];
    const noun = KILL_NOUNS[target.kind];
    if (!noun) return [];
    return [target.side === playerSide ? `${OOPS}${noun}` : `${SCRATCH_ONE}${noun}`];
  });
}

/**
 * `You just shot at one of ours!` — `RB.EXE` 677, `s\combat\collide.c`, the
 * string that feeds the Board of Inquiry. Fired on rounds landing on a friend,
 * not on the kill, which is the whole point of it: it is the warning that comes
 * before the tribunal does.
 */
function friendlyFireWarning(next: EncounterState, playerId: string, playerSide: EncounterSide): readonly string[] {
  const hit = next.events.some(
    (event) =>
      event.kind === "takingHits" &&
      event.sourceId === playerId &&
      event.aircraftId !== playerId &&
      next.aircraft.find((entry) => entry.id === event.aircraftId)?.side === playerSide,
  );
  return hit ? [SHOT_AT_ONE_OF_OURS] : [];
}

/**
 * The two cues only a *mission* can answer, which the bare encounter has no way
 * to know. Both are edge flags rather than levels: the caller decides the tick
 * they fire, because it is the caller that holds the previous `MissionRun`.
 * Omitting them entirely keeps the old behaviour exactly, which is what `/vcr`
 * and `/duel` do.
 */
export interface CalloutContext {
  /** Set on the tick a mission's outcome first becomes `success`. */
  readonly missionCompleted?: boolean;
  /** Set on the tick the player first comes inside `NEARING_RANGE_M` of what the mission is about. */
  readonly nearing?: MissionType;
}

/**
 * The same thing for a whole encounter: the player's own flight messages, then
 * the combat feed. Combat lines come second because "YOUR WINGS HAVE BROKEN!"
 * is the news and "ENEMY DESTROYED" is the consequence.
 */
export function encounterMessages(
  previous: EncounterState,
  next: EncounterState,
  playerId: string,
  context: CalloutContext = {},
): readonly string[] {
  const before = previous.aircraft.find((entry) => entry.id === playerId);
  const after = next.aircraft.find((entry) => entry.id === playerId);
  const flight = before && after ? messagesForTick(before.sim, after.sim) : [];
  const batteries = new Set(next.flak.map((battery) => battery.id));
  const combat = next.events
    .map((event) => messageForCombatEvent(event, playerId, event.sourceId !== null && batteries.has(event.sourceId)))
    .filter((message): message is string => message !== null);
  const player = after ?? before;
  const side = player?.side;
  const nation = player ? nationOf(player.sim.config.aircraft) : undefined;
  const kills = side
    ? [
        ...aircraftKills(next, playerId, side),
        ...targetKills(previous, next, playerId, side),
        ...friendlyFireWarning(next, playerId, side),
      ]
    : [];
  // The named line *replaces* the flat one rather than following it: G33's whole
  // complaint is that every kill read `ENEMY DESTROYED` regardless of what went
  // down. Somebody else's kill still reads it, because the port has no recovered
  // string for a victory that is not yours. (`audio/sfx-map.ts` routes the named
  // lines to the same explosion cue, so the swap is silent in the mix.)
  const named = kills.length > 0;
  const feed = named ? combat.filter((message) => message !== OUTCOME_BANNERS.enemyDown) : combat;
  const nearing = context.nearing ? NEARING_MESSAGES[context.nearing] : undefined;
  return [
    ...flight,
    ...(nation ? situationalCallouts(previous, next, playerId, nation) : []),
    ...(nearing ? [nearing] : []),
    ...feed,
    ...kills,
    ...(context.missionCompleted ? [MISSION_COMPLETE] : []),
  ];
}
